// CRITICAL: All imports must be static (not dynamic) to ensure proper loading order
import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { logger } from './lib/logger';
import { errorTracker } from './lib/error-tracking';
import { initPerformanceMonitoring } from './lib/performance-monitoring';

// Import CSS
import './index.css';
import './styles/mobile-fixes.css';

// Debug mode: visual issue detection (dev only) - enable when auditing
// if (import.meta.env.DEV) {
//   import('./debug.css');
// }

// PERFORMANCE: Stream Video SDK CSS (~100KB) moved to load only when video features are used
// See VideoProviderWrapper in App.tsx - loads on first video context use

// Extend Window interface for our app ready signal
declare global {
  interface Window {
    hsAppReady?: () => void;
  }
}

// PERFORMANCE: Track app initialization timing
const appStartTime = performance.now();

// Minimal error display for catastrophic failures
function showCriticalError(message: string, details?: string) {
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%); padding: 20px; font-family: system-ui, -apple-system, sans-serif;">
        <div style="max-width: 400px; background: white; border-radius: 16px; padding: 32px; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
          <div style="width: 64px; height: 64px; background: #fef3c7; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <path d="M12 9v4"/>
              <path d="M12 17h.01"/>
            </svg>
          </div>
          <h1 style="font-size: 24px; font-weight: bold; color: #1f2937; margin-bottom: 16px;">Unable to Load</h1>
          <p style="color: #6b7280; margin-bottom: 24px;">${message}</p>
          ${details ? `<p style="font-size: 12px; color: #9ca3af; margin-bottom: 24px; word-break: break-word;">${details}</p>` : ''}
          <button onclick="window.location.reload()" style="background: #7c3aed; color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 16px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
            </svg>
            Reload Page
          </button>
        </div>
      </div>
    `;
  }
}

// Initialize app
function initializeApp() {
  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      throw new Error("Root element not found");
    }

    // Mark app as initialized to prevent error screen for non-critical errors
    // (hsAppReady is now called from App mount - ensures loading hides only after React renders)
    if (typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).__HS_APP_INITIALIZED__ = true;
    }

    // Render the app
    createRoot(rootElement).render(
      <ErrorBoundary>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </ErrorBoundary>
    );

    // PERFORMANCE: Log initialization time in development
    if (import.meta.env.DEV) {
      const initTime = performance.now() - appStartTime;
      logger.info(`[HoleScale] App initialized in ${initTime.toFixed(0)}ms`);
    }

    // PERFORMANCE: Initialize non-critical services after app is rendered
    // Use requestIdleCallback for low-priority initialization
    const initNonCritical = () => {
      // Initialize performance monitoring
      try {
        initPerformanceMonitoring();
      } catch (e) {
        logger.warn('[HoleScale] Performance monitoring init failed', { error: e });
      }

      // Error tracking - now using static import to avoid dynamic/static import conflict
      try {
        errorTracker.initialize();
      } catch (e) {
        logger.warn('[HoleScale] Error tracker init failed', { error: e });
      }

      // Register service worker for PWA and caching
      if ('serviceWorker' in navigator && import.meta.env.PROD) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/sw.js')
            .then((registration) => {
              logger.info('[HoleScale] Service Worker registered', { scope: registration.scope });
            })
            .catch((error) => {
              logger.warn('[HoleScale] Service Worker registration failed', { error });
            });
        });
      }
    };

    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number }).requestIdleCallback(initNonCritical, { timeout: 5000 });
    } else {
      setTimeout(initNonCritical, 1000);
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Log full error details for debugging
    logger.error('[HoleScale] Critical initialization error', {
      message: errorMessage,
      stack: errorStack,
      error,
    });

    showCriticalError(
      'The application failed to start. Please try reloading the page.',
      import.meta.env.DEV ? errorMessage : undefined
    );

    // Also try to show error in the HTML error state
    const errorDiv = document.getElementById('hs-error');
    const errorMsg = document.getElementById('hs-error-msg');
    const loading = document.getElementById('hs-loading');

    if (errorDiv && errorMsg && loading) {
      loading.classList.add('hs-hidden');
      errorDiv.classList.remove('hs-hidden');
      errorMsg.textContent = `Error: ${errorMessage}`;
    }
  }
}

// Start the app immediately
initializeApp();

// Fallback: Hide loading screen after 10 seconds if app doesn't load
// This prevents users from being stuck on the loading screen
setTimeout(() => {
  const loading = document.getElementById('hs-loading');
  const root = document.getElementById('root');

  if (loading && !loading.classList.contains('hs-hidden') && root) {
    logger.warn('[HoleScale] App initialization timeout - showing app anyway');
    // Try to show the app even if initialization didn't complete
    if (typeof window.hsAppReady === 'function') {
      window.hsAppReady();
    } else {
      // Fallback: manually hide loading and show root
      loading.classList.add('hs-hidden');
      root.classList.remove('hs-hidden');
    }
  }
}, 10000); // 10 second timeout
